import { ArrowLeft, ChevronDownCircle, Bell, MessageCircle, Search, AlertTriangle, X } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionContext";
import { MODULE_CODES } from "../constants/modules";
import { TOP_NAV_GROUPS, NavigationGroupId } from "../constants/navigationCatalogue";
import NavbarMenu from "./NavbarMenu";
import ThemeModal from "./ThemeModal";
import TopDefinitionsMenu from "./TopDefinitionsMenu";
import TopMaterialMenu from "./TopMaterialMenu";
import TopManufacturingMenu from "./TopManufacturingMenu";
import TopNonMaterialMenu from "./TopNonMaterialMenu";
import {
  TopFinanceMenu,
  TopInvoiceMenu,
  TopPlanningMenu,
  TopSupportSectorsMenu,
} from "./TopRemainingMenus";
import TopStockManagementMenu from "./TopStockManagementMenu";
import { useTranslation } from "react-i18next";
import useUnreadMessageCount from "../hooks/useUnreadMessageCount";
import useBarcodeExpiry from "../hooks/useBarcodeExpiry";
import useNumberFormatSettings from "../hooks/useNumberFormatSettings";
import { useQueryInspector } from "../context/QueryInspectorContext";
import { TopNavMenuProvider } from "../context/TopNavMenuContext";
import GlobalSearchModal from "./GlobalSearchModal";

const NAV_ICON_BUTTON =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition hover:text-cyan-300 focus:outline-none";

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationItems, setNotificationItems] = useState<typeof unreadMessages>([]);
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const { count: unreadCount, messages: unreadMessages, refresh: refreshUnreadMessages, markRead } = useUnreadMessageCount();
  const { enabled, toggle } = useQueryInspector();
  const { hasAccess } = usePermissions();
  const barcodeExpiry = useBarcodeExpiry();
  useNumberFormatSettings();
  const showBarcodeExpiryWarning = !!barcodeExpiry && hasAccess(MODULE_CODES.BARCODE_ALERTS);
  // A user can be granted BARCODE_ALERTS without COMPANY_SETTINGS (e.g. QA sector) —
  // only send them to /company-settings when they can actually open it, otherwise
  // ProtectedRoute would bounce them straight back to /dashboard.
  const canOpenCompanySettings = hasAccess(MODULE_CODES.COMPANY_SETTINGS);

  const handleLogout = () => {
    logout();
  };

  // Bell click/keypress — go to /dashboard and tell the Communication hub to
  // switch to the Inbox tab (needed even when already on /dashboard, since
  // navigating to the current route is a no-op for React Router).
  const openCommunicationInbox = () => {
    navigate('/dashboard');
    window.dispatchEvent(new CustomEvent('openCommunicationInbox'));
  };

  const openNotifications = () => {
    const displayed = unreadMessages.slice(0, 5);
    setNotificationItems(displayed);
    setDropdownOpen(false);
    setNotificationsOpen(true);
    // Do NOT mark as read on open — badge stays until the user opens a message
  };

  const toggleUserMenu = () => {
    setNotificationsOpen(false);
    setDropdownOpen((open) => !open);
  };

  const openNotification = (message: (typeof notificationItems)[number]) => {
    setNotificationsOpen(false);
    void markRead(message.id).catch(() => undefined).then(() => refreshUnreadMessages());
    const metadata = message.metadata;
    if (message.notificationType === 'machine_repair_request' && metadata?.rnWorkOrderNumber) {
      navigate('/work-order/6020', {
        state: { workOrderId: metadata.rnWorkOrderId, machineRepairRequest: metadata },
      });
      return;
    }
    // Рекламација / QP / Узоркување threads live on the РН — open that work order.
    if (
      ['rn_reclamation_request', 'rn_qp_request', 'rn_uzurkuvanje_request'].includes(message.notificationType || '')
      && metadata?.rnWorkOrderId
    ) {
      navigate('/work-order/6020', { state: { workOrderId: metadata.rnWorkOrderId } });
      return;
    }
    // Продажба испратила месечен план — Планирање → Производство → Месечен на тој годишен план.
    if (message.notificationType === 'monthly_plan_sales_sent' && metadata?.parentDocument) {
      navigate('/planning/01', {
        state: {
          loadDocumentId: metadata.parentDocument,
          openProductionMonthly: true,
          monthlyPlanId: metadata.monthlyPlanId,
          navRequestId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        },
      });
      return;
    }
    // Производство одговори со рокови: Планирање, Продажба, Месечен на тој продажен месец.
    if (message.notificationType === 'monthly_plan_production_responded' && metadata?.parentDocument) {
      navigate('/planning/01', {
        state: {
          loadDocumentId: metadata.parentDocument,
          openSalesMonthly: true,
          salesYear: metadata.salesYear,
          salesMonth: metadata.salesMonth,
          navRequestId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        },
      });
      return;
    }
    // Производство го испрати материјалното сумарно: Планирање, Набавка, Месечен на тој план.
    if (message.notificationType === 'monthly_plan_material_sent' && metadata?.parentDocument) {
      navigate('/planning/01', {
        state: {
          loadDocumentId: metadata.parentDocument,
          openProcurementMonthly: true,
          monthlyPlanId: metadata.monthlyPlanId,
          navRequestId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        },
      });
      return;
    }
    // Набавка одговори со рокови: Планирање, Производство, Месечен, Материјално сумарно на тој план.
    if (message.notificationType === 'monthly_plan_procurement_responded' && metadata?.parentDocument) {
      navigate('/planning/01', {
        state: {
          loadDocumentId: metadata.parentDocument,
          openProductionSummary: true,
          monthlyPlanId: metadata.monthlyPlanId,
          navRequestId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        },
      });
      return;
    }
    // Набавка одговори: Планирање, Финансии, Месечен на тој продажен месец (рок на плаќање).
    if (message.notificationType === 'monthly_plan_finance_pending' && metadata?.parentDocument) {
      navigate('/planning/01', {
        state: {
          loadDocumentId: metadata.parentDocument,
          openFinanceMonthly: true,
          monthlyPlanId: metadata.monthlyPlanId,
          salesYear: metadata.salesYear,
          salesMonth: metadata.salesMonth,
          navRequestId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        },
      });
      return;
    }
    // Финансии го затворија планот: оди до секторот до кој корисникот има пристап
    // (Продажба → Производство → Набавка), инаку само го отвора документот.
    if (message.notificationType === 'monthly_plan_finance_closed' && metadata?.parentDocument) {
      const navRequestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      let target: Record<string, unknown> = {};
      if (hasAccess(MODULE_CODES.PLANNING_SALES)) {
        target = { openSalesMonthly: true, salesYear: metadata.salesYear, salesMonth: metadata.salesMonth };
      } else if (hasAccess(MODULE_CODES.PLANNING_PRODUCTION)) {
        target = { openProductionMonthly: true, monthlyPlanId: metadata.monthlyPlanId };
      } else if (hasAccess(MODULE_CODES.PLANNING_PROCUREMENT_VM)) {
        target = { openProcurementMonthly: true, monthlyPlanId: metadata.monthlyPlanId };
      }
      navigate('/planning/01', {
        state: { loadDocumentId: metadata.parentDocument, ...target, navRequestId },
      });
      return;
    }
    // Барање до КК (from Магацин) — open the КК ledger.
    if (message.notificationType === 'qc_request') {
      navigate('/quality-control');
      return;
    }
    // Заклучок од КК (request signed) — open Магацин → Табла.
    if (message.notificationType === 'qc_conclusion') {
      navigate('/storehouse/search-board');
      return;
    }
    navigate('/dashboard');
    window.dispatchEvent(new CustomEvent('openCommunicationInbox'));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        setNotificationsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchModalOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const menuById: Record<NavigationGroupId, React.ReactNode> = {
    definitions: <TopDefinitionsMenu />,
    planning: <TopPlanningMenu />,
    procurement: <TopMaterialMenu />,
    stockManagement: <TopStockManagementMenu />,
    warehouse: <TopStockManagementMenu variant="warehouse" />,
    manufacturing: <TopManufacturingMenu />,
    sales: <TopNonMaterialMenu />,
    supportSectors: <TopSupportSectorsMenu />,
    financial: <TopFinanceMenu />,
  };

  return (
    <>
    <nav
      className="px-6 py-2.5 flex justify-between items-center text-white fixed top-0 left-0 right-0 z-[10001]"
      style={{ background: `linear-gradient(to right, var(--color-primary), var(--color-secondary))` }}
    >
      <TopNavMenuProvider>
      <div className="flex items-center gap-2">
        {/* Back button */}
        <button
          title={t('common.back')}
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
          className="p-2 bg-black/20 text-white rounded-md shadow-lg hover:bg-black/30 transition-colors backdrop-blur-sm"
          type="button"
        >
          <ArrowLeft size={20} />
        </button>

        {TOP_NAV_GROUPS.map(({ id }) => <React.Fragment key={id}>{menuById[id]}</React.Fragment>)}
        <div className="hidden"><TopInvoiceMenu /></div>
      </div>
      </TopNavMenuProvider>

      <div className="flex items-center gap-4">
        <div className="relative" ref={dropdownRef}>
          <div className="flex items-center gap-3">
            {/* Desktop Search Bar Trigger */}
            <div
              onClick={() => setSearchModalOpen(true)}
              className="relative hidden lg:flex items-center cursor-pointer group"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSearchModalOpen(true);
                }
              }}
            >
              <Search size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-blue-500 transition-colors" />
              <input
                type="text"
                aria-label="Search"
                className="h-9 w-80 cursor-pointer rounded-md border border-white/40 bg-white pl-9 pr-14 text-sm text-gray-800 shadow-sm outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500/50"
                placeholder={t('navbar.searchPlaceholder', 'Пребарај...')}
                readOnly
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[10px] font-semibold text-gray-400 border border-gray-200 bg-gray-50 rounded pointer-events-none">
                Ctrl K
              </span>
            </div>

            {/* Mobile / Tablet Search Icon */}
            <button
              title={t('navbar.searchPlaceholder', 'Пребарај...')}
              aria-label="Search"
              type="button"
              onClick={() => setSearchModalOpen(true)}
              className="lg:hidden p-2 text-white/80 hover:text-white rounded-md hover:bg-white/10 transition-colors focus:outline-none"
            >
              <Search size={20} />
            </button>

            <div className="flex items-center gap-0.5">
            <button
              title={t("sidebar.communication", "Комуникации")}
              className={NAV_ICON_BUTTON}
              aria-label={t("sidebar.communication", "Комуникации")}
              type="button"
              onClick={() => navigate("/dashboard")}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") navigate("/dashboard");
              }}
            >
              <MessageCircle size={22} />
            </button>

            {/* Barcode registration expiry warning — navigates to /company-settings
                only when the user can actually open it; otherwise it's an
                informational-only icon (tooltip, no click/keyboard affordance). */}
            {showBarcodeExpiryWarning && barcodeExpiry && (() => {
              const isExpired = barcodeExpiry.status === 'expired';
              const message = isExpired
                ? t('settings.barcodeExpiryExpired', { date: barcodeExpiry.displayDate })
                : t('settings.barcodeExpiryWarning', { date: barcodeExpiry.displayDate, count: barcodeExpiry.daysRemaining });
              const colorClass = isExpired ? 'text-red-400' : 'text-yellow-300';

              if (!canOpenCompanySettings) {
                return (
                  <span
                    title={message}
                    aria-label={message}
                    role="img"
                    className={`flex items-center justify-center rounded-full cursor-default ${colorClass}`}
                  >
                    <AlertTriangle size={22} />
                  </span>
                );
              }

              return (
                <button
                  title={message}
                  aria-label={message}
                  className={`transition flex items-center justify-center rounded-full focus:outline-none ${colorClass} ${
                    isExpired ? 'hover:text-red-300' : 'hover:text-yellow-200'
                  }`}
                  type="button"
                  onClick={() => navigate('/company-settings')}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') navigate('/company-settings');
                  }}
                >
                  <AlertTriangle size={22} />
                </button>
              );
            })()}

            {/* Bell — unread inbox count, navigates to /dashboard and opens the Inbox tab */}
            <div className="relative flex items-center">
            <button
              title={t('notifications.inbox', 'Inbox')}
              className={`${NAV_ICON_BUTTON} relative group`}
              aria-label={t('notifications.inbox', 'Inbox')}
              type="button"
              onClick={() => notificationsOpen ? setNotificationsOpen(false) : openNotifications()}
              tabIndex={0}
            >
              <Bell size={22} className="text-white group-hover:text-cyan-300 transition-colors" />
              {unreadCount > 0 && (
                <span
                  className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 shadow-md border border-white select-none"
                  style={{ minWidth: 20, minHeight: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {notificationsOpen && (
              <div className="absolute right-0 top-10 z-[10002] w-80 overflow-hidden rounded-lg bg-white text-gray-800 shadow-xl ring-1 ring-black/10">
                <div className="flex items-center justify-between border-b px-3 py-2">
                  <span className="text-sm font-semibold">{t('notifications.inbox', 'Notifications')}</span>
                  <button type="button" onClick={() => setNotificationsOpen(false)} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label={t('common.close', 'Close')}><X size={16} /></button>
                </div>
                {notificationItems.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-gray-500">{t('notifications.empty', 'Нема нови нотификации')}</div>
                ) : notificationItems.map((message) => (
                  <div key={message.id} className="group flex gap-2 border-b px-3 py-2 hover:bg-gray-50">
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={() => openNotification(message)}>
                      <div className="truncate text-xs font-semibold">{message.subject}</div>
                      <div className="truncate text-xs text-gray-600">{message.snippet || message.from}</div>
                    </button>
                    <button type="button" className="self-start rounded p-1 text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-gray-200 hover:text-gray-700" onClick={() => setNotificationItems((items) => items.filter((item) => item.id !== message.id))} aria-label={t('common.close', 'Close')}><X size={14} /></button>
                  </div>
                ))}
                <button type="button" onClick={openCommunicationInbox} className="w-full px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50">{t('notifications.viewAll', 'Види ги сите')}</button>
              </div>
            )}
            </div>

            {/* Settings dropdown button */}
            <button
              title="Settings"
              className={NAV_ICON_BUTTON}
              aria-label="Settings"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleUserMenu();
              }}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') toggleUserMenu();
              }}
            >
              <ChevronDownCircle size={22} />
            </button>
            </div>
          </div>

          {dropdownOpen && (
            <NavbarMenu
              user={user}
              onLogout={handleLogout}
              queryInspectorEnabled={enabled}
              onToggleQueryInspector={toggle}
              onOpenTheme={() => {
                setDropdownOpen(false);
                setThemeModalOpen(true);
              }}
              onOpenHelp={() => {
                setDropdownOpen(false);
                navigate("/documentation-help");
              }}
            />
          )}
        </div>
      </div>
    </nav>
    <ThemeModal show={themeModalOpen} onClose={() => setThemeModalOpen(false)} />
    <GlobalSearchModal isOpen={searchModalOpen} onClose={() => setSearchModalOpen(false)} />
    </>
  );
};

export default Navbar;
